import { useCallback, useTransition, useMemo } from "react";
import {
  CustomDialog,
  Header,
  Content,
  Footer,
  Text,
  Image,
  ButtonGroup,
  Button,
  CloseButton,
  Skeleton,
  FileTrigger,
  ProgressCircle,
  ActionButton,
} from "@react-spectrum/s2";
import { ListBox, ListBoxItem } from "react-aria-components";
import { useAsyncList } from "react-stately";
import { useStore } from "zustand";
import { getElements } from "@app/services/ccLibraries";
import { getPresignedImageUrlFromTemplatedLink } from "@app/utils";

import { store } from "../../store";
import { useStory } from "@app/hooks/stories";
import { batchPresignURLs, uploadFileByBlob } from "@app/services/s3";

import AIGenerateIcon from "icon:@app/assets/AIGenerate.svg";
import DropToUpload from "@react-spectrum/s2/illustrations/linear/DropToUpload";

import {
  style,
  lightDark,
} from "@react-spectrum/s2/style" with { type: "macro" };

const fetchDirectory = async (directory, imageReference) => {
  try {
    const { urls } = await batchPresignURLs(directory);

    const currentImageReference = imageReference;

    return {
      items: urls.map((url) => ({
        name: url.filePath.split("/").slice(-1)[0],
        filePath: url.filePath,
        url: url.presignURL,
        internalUrl: url.internalURL,
      })),
      selectedKeys: new Set(
        currentImageReference ? [currentImageReference?.filePath] : [],
      ),
    };
  } catch (error) {
    console.error("Failed to load styles:", error);
    return { items: [], selectedKeys: new Set() };
  }
};

const fetchBrandStyles = async (brand) => {
  if (!brand?.libraryId) {
    return [];
  }

  try {
    const data = await getElements({
      libraryId: brand.libraryId,
    });

    return {
      items: data?.elements.map((element) => ({
        name: element.name,
        filePath: element?.thumbnail?.componentId,
        url: getPresignedImageUrlFromTemplatedLink(
          element?.thumbnail?.templatedLink,
          element?.thumbnail?.componentId,
        ),
      })),
      selectedKeys: new Set([]),
    };
  } catch (error) {
    console.error("Failed to fetch brand elements:", error);
    return [];
  }
};

const ThumbnailList = ({ onImageSelect, isUploading, ...props }) => {
  return (
    <ListBox
      {...props}
      className={style({
        width: "full",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        gap: 16,
        width: "[fit-content]",
        maxWidth: "100%",
      })}
    >
      {(item) => (
        <ListBoxItem
          id={item.filePath}
          textValue={item.name}
          className={style({
            aspectRatio: "4/3",
            borderRadius: "lg",
            overflow: "hidden",
            outlineColor: {
              default: "transparent",
              isHovered: lightDark(
                "transparent-black-400",
                "transparent-white-400",
              ),
              isFocused: lightDark(
                "transparent-black-600",
                "transparent-white-600",
              ),
              isSelected: "gray-900",
            },
            outlineOffset: 2,
            outlineStyle: "solid",
            outlineWidth: 2,
            // borderColor: {
            //   default: "transparent",
            //   isSelected: "yellow-900",
            // },
            // borderWidth: 2,
            // borderStyle: "solid",
            transition: "transform",
            willChange: "transform",
            transform: {
              default: "none",
              isPressed: "perspective(160px) translate3d(0, 0, -2px)",
            },
          })}
        >
          {item.name !== "NEW" ? (
            <Image
              src={item.url}
              alt={item.name}
              styles={style({
                size: "full",
                objectFit: "cover",
              })}
            />
          ) : (
            <FileTrigger acceptedFileTypes="image/*" onSelect={onImageSelect}>
              <ActionButton
                isDisabled={isUploading}
                UNSAFE_className={style({
                  size: "full",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  padding: 16,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                })}
              >
                {!isUploading ? (
                  <DropToUpload styles={style({ size: 48 })} />
                ) : (
                  <ProgressCircle
                    isIndeterminate
                    size="L"
                    staticColor="auto"
                    styles={style({ alignSelf: "center" })}
                  />
                )}
                <Text styles={style({ font: "ui-sm", textAlign: "center" })}>
                  {!isUploading
                    ? "Drag and drop or click to select"
                    : "Uploading..."}
                </Text>
              </ActionButton>
            </FileTrigger>
          )}
        </ListBoxItem>
      )}
    </ListBox>
  );
};

export const StoryStyleDialog = ({ storyId, onPrimaryAction }) => {
  const [isUploading, startUploading] = useTransition();

  const email = useStore(store, (state) => state.ims?.profile?.email);

  const storyQuery = useStory(storyId);
  const story = storyQuery.data;

  const isBrandSelected = useMemo(() => {
    return story?.brand?.libraryId;
  }, [story]);

  const recommendedList = useAsyncList({
    getKey: (item) => item.filePath,
    async load() {
      return fetchDirectory("/referenceStyle/", storyId);
    },
  });

  const userList = useAsyncList({
    getKey: (item) => item.filePath,
    async load() {
      try {
        const { items, selectedKeys } = await fetchDirectory(
          `/${email.split("@")[0]}/referenceStyle/`,
          storyId,
        );

        items.push({
          name: "NEW",
          filePath: "NEW",
          url: "",
          internalURL: "",
        });

        return { items, selectedKeys };
      } catch (error) {
        console.error("Failed to load user styles:", error);
        return {
          items: [
            {
              name: "NEW",
              filePath: "NEW",
              url: "",
              internalURL: "",
            },
          ],
          selectedKeys: new Set(),
        };
      }
    },
  });

  const brandsList = useAsyncList({
    getKey: (item) => item.filePath,
    async load() {
      try {
        const { items, selectedKeys } = await fetchBrandStyles(story?.brand);

        console.log("Brand styles:", items, story?.brand);

        return { items, selectedKeys };
      } catch (error) {
        console.error("Failed to load brand styles:", error);
        return { items: [], selectedKeys: new Set() };
      }
    },
  });

  const isLoading =
    recommendedList.isLoading || userList.isLoading || brandsList.isLoading;

  const setImageReference = useStore(store, (state) => state.setImageReference);

  const handleSelectionChange = (selectedKeys, list) => {
    recommendedList.setSelectedKeys(new Set());
    userList.setSelectedKeys(new Set());
    brandsList.setSelectedKeys(new Set());

    list.setSelectedKeys(selectedKeys);

    const imageReference = list.getItem(selectedKeys.currentKey);

    setImageReference(storyId, imageReference ?? null);
  };

  const handleImageSelect = useCallback(
    (files) => {
      if (files.length > 0) {
        startUploading(async () => {
          const [file] = files;

          const filePath = `${email.split("@")[0]}/referenceStyle/${file.name}`;

          try {
            const response = await uploadFileByBlob(filePath, file);

            if (response?.presignURL) {
              // Create a new image reference object
              const newImageReference = {
                name: file.name,
                filePath: filePath,
                url: response.presignURL,
                internalUrl: response.presignURL,
              };

              // Insert the new item before the placeholder item where item.name === 'NEW'
              const placeholderIndex = userList.items.findIndex(
                (item) => item.name === "NEW" || item.filePath === "NEW",
              );
              if (placeholderIndex >= 0) {
                userList.insert(placeholderIndex, newImageReference);
              } else {
                userList.append(newImageReference);
              }

              // Set it as the selected image reference
              setImageReference(storyId, newImageReference);

              // Select the new item
              userList.setSelectedKeys(new Set([filePath]));

              // Clear other selections
              recommendedList.setSelectedKeys(new Set());
              brandsList.setSelectedKeys(new Set());
            }
          } catch (error) {
            console.error("Error uploading file:", error);
          }
        });
      }
    },
    [
      storyId,
      email,
      userList,
      setImageReference,
      startUploading,
      recommendedList,
      brandsList,
    ],
  );

  const renderEmptyState = (isLoading, children) => {
    if (isLoading) {
      return (
        <>
          {new Array(3).fill(null).map((_, index) => (
            <Skeleton key={index} isLoading>
              <div
                alt="Loading style"
                className={style({
                  aspectRatio: "4/3",
                  borderRadius: "lg",
                  overflow: "hidden",
                })}
              >
                <Image
                  styles={style({
                    size: "full",
                    objectFit: "cover",
                  })}
                />
              </div>
            </Skeleton>
          ))}
        </>
      );
    }

    return (
      <div
        className={style({
          aspectRatio: "4/3",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          font: "ui-sm",
        })}
      >
        {children || "No styles found"}
      </div>
    );
  };

  return (
    <CustomDialog
      isDismissable
      styles={style({
        maxWidth: "90vw",
        width: {
          default: "full",
          sm: 1200,
        },
      })}
    >
      {({ close }) => (
        <div
          className={style({
            size: "full",
            display: "grid",
            gridTemplateAreas: [
              "header header header",
              ". . .",
              "content content content",
              ". . .",
              "footer footer buttonGroup",
            ],
            gridTemplateRows: ["auto", 16, "1fr", 32, "auto"],
          })}
        >
          <Header
            styles={style({
              gridArea: "header",
              font: "title-2xl",
              marginTop: 0,
            })}
          >
            <Text>Select style</Text>
          </Header>
          <Content styles={style({ gridArea: "content" })}>
            <div
              className={style({
                display: "flex",
                flexDirection: "column",
                gap: 16,
              })}
            >
              <div>
                <h2 className={style({ font: "detail-sm" })}>Recommended</h2>
                <ThumbnailList
                  aria-label="Select generated images visual style"
                  items={recommendedList.items}
                  selectionMode="single"
                  selectedKeys={recommendedList.selectedKeys}
                  onSelectionChange={(keys) =>
                    handleSelectionChange(keys, recommendedList)
                  }
                  layout="grid"
                  renderEmptyState={() =>
                    renderEmptyState(recommendedList.isLoading)
                  }
                />
              </div>

              <div>
                <h2 className={style({ font: "detail-sm" })}>Brand styles</h2>
                <ThumbnailList
                  aria-label="Select generated images visual style"
                  items={brandsList.items}
                  selectionMode="single"
                  selectedKeys={brandsList.selectedKeys}
                  onSelectionChange={(keys) =>
                    handleSelectionChange(keys, brandsList)
                  }
                  layout="grid"
                  renderEmptyState={() =>
                    renderEmptyState(
                      brandsList.isLoading,
                      isBrandSelected ? null : <Text>No brand selected</Text>,
                    )
                  }
                />
              </div>

              <div>
                <h2 className={style({ font: "detail-sm" })}>Your styles</h2>
                <ThumbnailList
                  aria-label="Select generated images visual style"
                  items={userList.items}
                  selectionMode="single"
                  selectedKeys={userList.selectedKeys}
                  onSelectionChange={(keys) =>
                    handleSelectionChange(keys, userList)
                  }
                  layout="grid"
                  renderEmptyState={() => renderEmptyState(userList.isLoading)}
                  onImageSelect={handleImageSelect}
                  isUploading={isUploading}
                />
              </div>
            </div>
          </Content>
          <Footer styles={style({ gridArea: "footer" })}></Footer>
          <ButtonGroup
            styles={style({
              gridArea: "buttonGroup",
              justifySelf: "end",
            })}
          >
            <Button
              variant="genai"
              isDisabled={recommendedList.isLoading}
              onPress={() => {
                onPrimaryAction?.();
                close();
              }}
            >
              <AIGenerateIcon />
              <Text>Generate images</Text>
            </Button>
          </ButtonGroup>
          <CloseButton
            styles={style({
              position: "absolute",
              top: 12,
              insetEnd: 12,
            })}
          />
        </div>
      )}
    </CustomDialog>
  );
};
